import { Component, Inject, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { registerLocaleData } from '@angular/common';
import localeCs from '@angular/common/locales/cs';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatIconRegistry } from '@angular/material/icon';
import { Store, select } from '@ngrx/store';
import { ThemeService } from '@zerops/zef/theme';
import { selectZefNgrxRouterIdByKey, zefGo } from '@zerops/zef/ngrx-router';
import { interval, combineLatest } from 'rxjs';
import {
  map,
  audit,
  filter,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  take,
} from 'rxjs/operators';
// import uniq from 'lodash-es/uniq';
import {
  ZefAuthState,
  selectTokenData,
  selectAuthState
} from '@zerops/zef/auth';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { selectZefWebsocketReconnecting } from '@zerops/zef/websocket';
import { outerRouteAnimation } from '@zerops/zef/animations';
import { Noola } from '@zerops/zef/noola';
import { ZefTranslationsService } from '@zerops/zef/translations';
import { CUSTOM_ICONS } from '@zerops/zef/shared-assets/custom-icons/custom-icons.constant';
import { ZefErrorsModule } from '@zerops/zef/errors';
import { ZefSnackModule } from '@zerops/zef/snack';
import { SatPopoverModule } from '@zerops/zef/popover';
import { ZefPipesModule } from '@zerops/zef/pipes';
import { ZuiDocsPopModule } from '@zerops/zui/docs-pop';
import { WebNotificationsPopModule } from '@zerops/zui/web-notifications-pop';
import { WebServiceStackActiveAppVersionPopModule } from '@zerops/zui/web-service-stack-active-app-version-pop';
import { WebPublicRoutingPopModule } from '@zerops/zui/web-public-routing-pop';
import { ZuiContextualDialogModule } from '@zerops/zui/contextual-dialog';
import { FeatureModule } from '@zerops/zerops/feature';
import { ZeElementsModule } from '@zerops/zerops/elements';
import { environment } from '@zerops/zerops/env';
import { UserEntity } from '@zerops/zerops/core/user-base';
// import { ProjectEntity } from '@zerops/zerops/core/project-base';
import {
  WsReconnectTranslations,
  WS_RECONNECT_FEATURE_NAME
} from '@zerops/zerops/feature/ws-reconnect';
import { WsReconnectModule } from '../feature/ws-reconnect';
import { VersionNotificationFeature } from '../feature/version-notification';
import { CrossTabSessionDialogFeature } from '../feature/cross-tab-session-dialog';
import { CodeServerOverlayFeature, codeServerOverlayClose } from '../feature/code-server-overlay';
// Deep import (not the barrel): `zcp-pool-claim-base`'s index.ts doesn't
// export this component yet (out of this slice's allowed file set — see
// zcp-onboard-wizard.component.ts's own doc comment on the mount being a
// separate slice's job) and re-exporting it there would still pull the
// barrel's other heavy re-exports into this bundle for no benefit.
import { ZcpOnboardWizardComponent } from '../core/zcp-pool-claim-base/zcp-onboard-wizard.component';
import { openTopUpDialogWithPromoCode } from '@zerops/zerops/feature/top-up-dialog';
import { VictoriaLogsOverlayFeature } from '../feature/victoria-logs-overlay';
import { OverlayTrayFeature } from '../feature/overlay-tray';
import { CommandPaletteFeature } from '../feature/command-palette';
import { AppPermissionsModule } from './app.permissions';
import { WebsocketDebuggerModule } from '../core/websocket-debugger';
import { menuPanelAnimation } from './app.animations';
import { AppState } from './app.model';
import { en, RouteKeys } from './app.constant';

registerLocaleData(localeCs, 'cs');

@Component({
  standalone: true,
  selector: 'z-app',
  templateUrl: './app.container.html',
  styleUrls: [ './app.container.scss' ],
  animations: [
    menuPanelAnimation(),
    outerRouteAnimation
  ],
  imports: [
    RouterOutlet,
    FeatureModule,
    AppPermissionsModule,
    ZefErrorsModule,
    ZefSnackModule,
    ZeElementsModule,
    SatPopoverModule,
    MatCardModule,
    ZefPipesModule,
    MatProgressSpinnerModule,
    WsReconnectModule,
    VersionNotificationFeature,
    ZuiDocsPopModule,
    WebsocketDebuggerModule,
    WebNotificationsPopModule,
    WebServiceStackActiveAppVersionPopModule,
    WebPublicRoutingPopModule,
    ZuiContextualDialogModule,
    CrossTabSessionDialogFeature,
    CodeServerOverlayFeature,
    VictoriaLogsOverlayFeature,
    OverlayTrayFeature,
    ZcpOnboardWizardComponent,
    CommandPaletteFeature
  ]
})
export class AppContainer extends ZefReactiveComponent implements OnInit {
  // # Data
  // -- sync
  authStates = ZefAuthState;
  googleSiteKey = environment.googleSiteKey;
  isProduction = environment.production;
  hideVersionWarning = environment.hideVersionWarning === 'true';
  menuPanelOpen = false;
  envName = environment.envName;
  currentProductionEnvName = environment.currentProductionEnvName;
  documentationPath = environment.documentationUrl;

  // -- async
  // TODO: not working properly, (true then false)
  // temporary fix
  // same in invitation.page.ts
  // probably related to delayWhen(([ s, id ]) => (s === ZefAuthState.Authorized && !id) ? timer(0) : EMPTY)
  // authorized$ = this._permission.authorize({
  //   only: [ Roles.Authorized ]
  // });
  authState$ = this._store.pipe(
    select(selectAuthState)
  );
  authData$ = this._store.pipe(select(selectTokenData));
  activeClientUser$ = this._userEntity.activeClientUser$;
  wsReconnectTranslations$ = this.translate$<WsReconnectTranslations>(WS_RECONNECT_FEATURE_NAME);
  wsReconnecting$ = combineLatest([
    this._store.pipe(select(selectZefWebsocketReconnecting)),
    this.authState$.pipe(map((s) => s === this.authStates.Authorized))
  ]).pipe(map(([ reconnecting, isAuthorized ]) => isAuthorized ? reconnecting : false));
  wsReconnectState$ = this.wsReconnecting$.pipe(
    map((reconnecting) => reconnecting ? '1' : '0')
  );
  wsReconnectShow$ = this.wsReconnecting$.pipe(
    audit((val) => interval(!!val ? 2000 : 3500))
  );

  // # State resolver
  state = this.$connect({
    authState: this.authState$,
    activeClientUser: this.activeClientUser$,
    wsReconnectTranslations: this.wsReconnectTranslations$,
    wsReconnectShow: this.wsReconnectShow$,
    wsReconnectState: this.wsReconnectState$,
    isCompact: this._theme.isCompact$
  });

  constructor(
    private _sanitizer: DomSanitizer,
    private _store: Store<AppState>,
    private _translationsService: ZefTranslationsService,
    private _iconRegistry: MatIconRegistry,
    private _noola: Noola,
    private _userEntity: UserEntity,
    private _theme: ThemeService,
    @Inject(DOCUMENT)
    private _document: Document
  ) {
    super();

    // add current release version to body tag
    this._document.body.classList.add('app-release-' + environment.appVersion);

    // photo mode: remove layout column scrolls for full-page screenshots
    if (localStorage.getItem('photoMode') === 'true') {
      this._document.body.classList.add('photo-mode');
    }

    // expanded cards: expand project cards to show all services without scrolling
    if (localStorage.getItem('expandedCards') === 'true') {
      this._document.body.classList.add('expanded-cards');
    }

    this._translationsService.setTranslations('general', { en });

    // custom icons
    CUSTOM_ICONS.forEach((name) => {
      this._iconRegistry.addSvgIcon(
        name,
        this._sanitizer.bypassSecurityTrustResourceUrl(
          `/assets/shared-assets/custom-icons/${name}.svg`
        )
      );
    });

    // add current theme as class
    this._theme.applyTheme();

  }

  // The onboarding wizard's failure state Continue button (spec-welcome-
  // mode.md §8.1): the wizard component already dropped its own layer
  // before emitting this — close the code-server overlay too and land on
  // the project detail page, never leaving a broken fullscreen iframe
  // behind. The project id comes from the router-id map (set for
  // RouteKeys.ProjectDetail on every project route entry, § app.routes.ts)
  // rather than being tracked here — if it's ever unknown (wizard closed
  // before any project route was entered), just close the overlay.
  /**
   * "Top up now" from the wizard's promo reveal. Opens the top-up dialog with
   * the code filled in, OVER the wizard layer — z-index 500 vs the CDK
   * overlay's 1000, the same band the agent auth dialog uses. The layer is
   * deliberately left up: dismissing the dialog returns to the reveal, and
   * Continue still leads to the agent roster, which has no other entry point.
   */
  onOnboardWizardTopUp(promoCode: string) {
    this._store.dispatch(openTopUpDialogWithPromoCode({ promoCode }));
  }

  onOnboardWizardContinue() {
    this._store.dispatch(codeServerOverlayClose());
    this._store.pipe(
      select(selectZefNgrxRouterIdByKey(RouteKeys.ProjectDetail)),
      take(1)
    ).subscribe((projectId) => {
      if (projectId) {
        this._store.dispatch(zefGo([ '/project', projectId ]));
      }
    });
  }

  ngOnInit() {

    super.ngOnInit();

    // Intercom was retired here in favour of Noola as the primary support
    // chat — see the Noola boot below. The ZefIntercomModule registration in
    // app.config.ts is commented out rather than deleted so this can be
    // reversed in one commit; `environment.enableIntercom` and the zef
    // intercom lib are both left intact but unreferenced.
    //
    // NOTE: `user.intercomHash` below is NOT Intercom residue. Noola's widget
    // key reuses Intercom's identity-verification secret, so that hash is what
    // authenticates the Noola session. Do not remove the field from the API.

    if (environment.enableNoola === 'true') {

      // boot Noola and identify the current user
      this.authData$
        .pipe(
          takeUntil(this.onDestroy$),
          filter((d) => !!d?.author && d?.author?.authorType !== 'BACKOFFICE'),
          switchMap(() => combineLatest([
            this.activeClientUser$.pipe(filter((d) => !!d)),
            // `intercomHash` lives ONLY on the top-level user entity. The `user`
            // nested inside ClientUser is not normalized (ClientUser declares no
            // `user` definition), so it is whatever /api/user/info embedded in
            // clientUserList[] — which carries no hash. Reading it from there
            // silently boots an unverified session.
            this._userEntity.activeUser$.pipe(filter((u) => !!u?.intercomHash))
          ]).pipe(
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
          )),
          map(([ { user, client }, activeUser ]) => ({
            email: user.email,
            user_id: user.id,
            name: user.fullName,
            // Noola reuses Intercom's identity-verification HMAC — the widget
            // key's identity_secret is set to Intercom's IV secret, so the same
            // server-minted hash validates under Noola's legacy user_hash scheme.
            user_hash: activeUser.intercomHash,
            // `company_id` is the dedup key — Noola matches on it before `name`.
            // That matters because `accountName` is user-editable (Settings ->
            // Organization name): keyed on the name alone, every rename forks a
            // new Noola company and orphans the old one's history. Keyed on our
            // client id, a rename just refreshes the display name in place.
            //
            // Identifying under a different company adds a membership rather
            // than moving the user (the first stays primary), so sending the
            // current client on every boot/update is safe.
            //
            // NOTE: this object shape needs Noola PR #62 deployed to prod. Until
            // it is, prod's identify reads `company` as a bare name string only
            // and will not link the org — see the string form in git history.
            company: {
              company_id: client?.id,
              name: client?.accountName
            }
          }))
        )
        .subscribe((data) => {
          this._noola.boot(data);
        });
    }

  }

}
